import styled, {css} from 'styled-components';
import Image from 'next/image';
import {parseError} from '@src/utils/error';
import {
  CSSProperties,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from './Modal';
import {useImageQuery} from '@src/queries/common.queires';
import {Skeleton} from './Skeleton';

export type ImageType =
  | 'COMPRESSED'
  | 'THUMBNAIL'
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'XLARGE'
  | 'VIDEO_THUMBNAIL';

type ApiImageProps = {
  imageId?: string;
  width?: number;
  height?: number;
  spinnerContainerStyle?: HTMLAttributes<HTMLDivElement>['style'];
  fill?: boolean;
  isPrevieable?: boolean;
  addEmptyImage?: boolean;
  /** Skeleton while remote image loads. Default: true. */
  showSkeleton?: boolean;
  /** CDN variant: thumbnail for lists, XLARGE for previews. */
  type?: ImageType;
} & Omit<React.HTMLProps<HTMLImageElement>, 'type'>;

const ErrorMessage = styled.div``;

const ImageWrap = styled.div<{
  $fill: boolean;
  $width?: number;
  $height?: number;
  $radius?: string | number;
}>`
  position: relative;
  overflow: hidden;
  line-height: 0;
  border-radius: ${(p) =>
    typeof p.$radius === 'number' ? `${p.$radius}px` : p.$radius ?? undefined};

  ${(p) =>
    p.$fill
      ? css`
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        `
      : css`
          display: block;
          width: ${p.$width ? `${p.$width}px` : '100%'};
          height: ${p.$height ? `${p.$height}px` : '100%'};
          flex-shrink: 0;
        `};
`;

const SkeletonLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;

  & > * {
    width: 100% !important;
    height: 100% !important;
  }
`;

function resolveRadius(style?: CSSProperties) {
  if (!style) {
    return undefined;
  }
  return style.borderRadius ?? style.borderTopLeftRadius;
}

/** Blob/object URLs must skip the Next optimizer or load events can stall. */
function isLocalObjectUrl(src: string) {
  return src.startsWith('blob:') || src.startsWith('data:');
}

export const ApiImage = ({
  imageId,
  width,
  height,
  style,
  isPrevieable = false,
  spinnerContainerStyle,
  showSkeleton = true,
  type = 'MEDIUM',
}: ApiImageProps) => {
  const hasImageId = Boolean(imageId);
  const {data, isLoading, isError, error} = useImageQuery(
    imageId as string,
    type
  );
  const [showModal, setShowModal] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  // The tile always renders a variant — the preview must stay full quality.
  const previewQuery = useImageQuery(
    isPrevieable && showModal ? (imageId as string) : '',
    'XLARGE'
  );

  const markReady = useCallback(() => {
    setImageReady(true);
  }, []);

  useEffect(() => {
    setImageReady(false);
  }, [imageId, data]);

  // Cache / Strict Mode / blob: onLoadingComplete sometimes never fires.
  // Never leave the skeleton up forever once we have a src.
  useEffect(() => {
    if (!data || imageReady) {
      return;
    }
    const fallback = window.setTimeout(markReady, 1200);
    return () => window.clearTimeout(fallback);
  }, [data, imageReady, markReady]);

  const isFill = !(width && height);
  const radius = resolveRadius(style as CSSProperties | undefined);
  const waitingForImage = isLoading || !data || !imageReady;
  const showLoadingSkeleton = showSkeleton && hasImageId && waitingForImage;
  const unoptimized = Boolean(data && isLocalObjectUrl(data));

  const imageStyle = useMemo(() => {
    const base: CSSProperties = {
      cursor: isPrevieable ? 'pointer' : 'default',
      ...(style as CSSProperties),
      opacity: imageReady ? 1 : 0,
      transition: 'opacity 180ms ease',
    };
    if (isFill) {
      return {
        ...base,
        width: '100%',
        height: '100%',
        objectFit: (style as CSSProperties)?.objectFit ?? 'cover',
      };
    }
    return base;
  }, [imageReady, isFill, isPrevieable, style]);

  if (!hasImageId) {
    return (
      <>
        {width && height ? (
          <Image
            src={'/images/mock-logo.png'}
            alt=""
            style={style}
            width={width}
            height={height}
          />
        ) : (
          <Image
            src={'/images/mock-logo.png'}
            alt=""
            style={style}
            fill
          />
        )}
      </>
    );
  }

  if (isError) {
    return <ErrorMessage>{parseError(error).message}</ErrorMessage>;
  }

  return (
    <>
      <ImageWrap
        $fill={isFill}
        $width={width}
        $height={height}
        $radius={radius}
        style={spinnerContainerStyle}
      >
        {showLoadingSkeleton ? (
          <SkeletonLayer aria-hidden>
            <Skeleton width="100%" height="100%" radius={0} />
          </SkeletonLayer>
        ) : null}
        {data ? (
          width && height ? (
            <Image
              key={data}
              onClick={() => isPrevieable && setShowModal(true)}
              src={data}
              alt=""
              style={imageStyle}
              width={width}
              height={height}
              unoptimized={unoptimized}
              onLoadingComplete={markReady}
              onError={markReady}
            />
          ) : (
            <Image
              key={data}
              onClick={() => isPrevieable && setShowModal(true)}
              src={data}
              alt=""
              style={imageStyle}
              fill
              unoptimized={unoptimized}
              onLoadingComplete={markReady}
              onError={markReady}
            />
          )
        ) : null}
      </ImageWrap>
      {isPrevieable && data ? (
        //@ts-ignore unneeded props are needed
        <Modal show={showModal} onClose={setShowModal}>
          <img
            src={previewQuery.data ?? data}
            style={{maxHeight: '80vh'}}
            alt=""
          />
        </Modal>
      ) : null}
    </>
  );
};
